// Investor service — admin-side access to investors and their Hub members.
import type {
  CreateHubMemberPayload,
  CreateInvestorPayload,
  HubMemberCreated,
  Investor,
  InvestorCreated,
  SetUserInvestorMembershipPayload,
  SetUserInvestorMembershipResult,
  UserInvestorMembership,
} from '../types/investor';
import { apiRequest } from '@startus/lib';

// The BE's standard `{ success, data }` envelope around every investors response.
interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export const investorsService = {
  /** Lists every investor (platform ADMIN only). */
  async listInvestors(): Promise<Investor[]> {
    const response = await apiRequest<ApiEnvelope<Investor[]>>('/api/v1/investors');
    return response.data;
  },

  /**
   * Creates an investor and its first SUPERADMIN atomically (platform ADMIN only).
   * 409 INVESTOR_SLUG_TAKEN / USER_ALREADY_EXISTS when slug or email are in use.
   */
  async createInvestor(payload: CreateInvestorPayload): Promise<InvestorCreated> {
    const response = await apiRequest<ApiEnvelope<InvestorCreated>>('/api/v1/investors', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response.data;
  },

  /** A user's current investor plus the facts the edit modal warns with (ADMIN only). */
  async getUserMembership(userId: string): Promise<UserInvestorMembership> {
    const response = await apiRequest<ApiEnvelope<UserInvestorMembership>>(
      `/api/v1/investors/memberships/users/${encodeURIComponent(userId)}`
    );
    return response.data;
  },

  /**
   * Assigns, moves, re-roles or removes a user's single investor (ADMIN only).
   * 409 INVESTOR_LAST_SUPERADMIN when it would leave an investor without a SUPERADMIN.
   */
  async setUserMembership(
    userId: string,
    payload: SetUserInvestorMembershipPayload
  ): Promise<SetUserInvestorMembershipResult> {
    const response = await apiRequest<ApiEnvelope<SetUserInvestorMembershipResult>>(
      `/api/v1/investors/memberships/users/${encodeURIComponent(userId)}`,
      { method: 'PUT', body: JSON.stringify(payload) }
    );
    return response.data;
  },

  /**
   * Creates a new user and adds them as a member of an existing investor.
   * ADMIN passes the investor-membership guard, so no membership is required.
   */
  async createHubMember(
    investorId: string,
    payload: CreateHubMemberPayload
  ): Promise<HubMemberCreated> {
    const response = await apiRequest<ApiEnvelope<HubMemberCreated>>(
      `/api/v1/investors/${encodeURIComponent(investorId)}/members/create-user`,
      { method: 'POST', body: JSON.stringify(payload) }
    );
    return response.data;
  },
};
